import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  Button,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import FileUpload from './FileUpload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { parseISO, isAfter, isBefore, format } from 'date-fns';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { API_BASE_URL } from '../components/api.js';

// IMPORTAÇÃO CORRETA DO CONTEXTO DE AUTENTICAÇÃO
import { useAuth } from '../components/AuthContext'; // Ajuste o caminho conforme necessário

dayjs.locale('pt-br');

const UploadBox = ({ label, onFileProcessed }) => {
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      onFileProcessed(uploadedFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const uploadedFile = event.dataTransfer.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      onFileProcessed(uploadedFile);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        textAlign: "center",
        border: "2px dashed",
        borderColor: file ? "success.main" : "text.secondary",
        backgroundColor: "background.paper",
        cursor: "pointer",
      }}
      onClick={() => fileInputRef.current && fileInputRef.current.click()}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <input
        type="file"
        hidden
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".xlsx, .xls"
      />
      <Stack direction="column" alignItems="center" spacing={2}>
        <CloudUploadIcon sx={{ fontSize: 40, color: "text.secondary" }} />
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {file ? file.name : `${label}: Clique ou arraste`}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default function MartinPedido() {
  const [estoqueFile, setEstoqueFile] = useState(null);
  const [swfastFile, setSwfastFile] = useState(null);
  const [dias, setDias] = useState(16);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [backendDates, setBackendDates] = useState([]);

  // USANDO O CONTEXTO DE AUTENTICAÇÃO CORRETAMENTE
  const { authenticatedFetch, logout } = useAuth();

  const ColoredCellRenderer = ({ value, colDef, data, api }) => {
    const getBackgroundColor = () => {
        const field = colDef.field;
        const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;

        if ((field === 'SWFAST' || field === 'Manter')){
          if( numericValue > 0) {
            return 'yellow';
          }
          else return 'transparent'
        }

        if ((field === 'Pela Saida' || field === 'Pela Media')){
          if(numericValue < 0) {
            return 'red';
          }
          else return 'transparent'
        }
        
        if (field === 'Saida' && data.SWFAST !== undefined) {
            const swfastValue = typeof data.SWFAST === 'string' ? parseFloat(data.SWFAST.replace(',', '.')) : data.SWFAST;
            if (numericValue < swfastValue) {
                return 'yellow';
            }
        }        

        return 'transparent';
    };

    return (
        <div style={{
            backgroundColor: getBackgroundColor(),
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px'
        }}>
            {value}
        </div>
    );
  };  

  const columnDefs = [
    { headerName: "Ordem", field: "Ordem", width: 70 },
    { headerName: "Fator", field: "Fator", width: 70, editable: true },
    { headerName: "Produtos", field: "Produtos", width: 320 },
    { headerName: "Média", field: "Media", width: 80 },
    { headerName: "Total", field: "Total", width: 80 },
    { headerName: "Estoque", field: "Estoque", width: 90, editable: true },
    { headerName: "Saida", field: "Saida", width: 80, editable: true, cellRenderer: ColoredCellRenderer },
    { headerName: "Pedido", field: "Pedido", width: 80, editable: true },
    { headerName: "SWFAST", field: "SWFAST", width: 90, cellRenderer: ColoredCellRenderer },
    { headerName: "Manter", field: "Manter", width: 90, editable: true, cellRenderer: ColoredCellRenderer },
    { headerName: "Pela Saida", field: "Pela Saida", width: 100, cellRenderer: ColoredCellRenderer },
    { headerName: "Pela Media", field: "Pela Media", width: 100, cellRenderer: ColoredCellRenderer },
    { headerName: "Saida1", field: "Saida1", hide: true },
    { headerName: "Saida2", field: "Saida2", hide: true },
    { headerName: "Saida3", field: "Saida3", hide: true },
  ];

  const today = dayjs();
  const latestBackendDate = dayjs('2025-08-20');

  useEffect(() => {
    const fetchInitialData = async () => {
      if (dias) {
        setLoading(true);
        try {
          const formattedDate = selectedDate.format('YYYY-MM-DD');
          
          // USANDO authenticatedFetch EM VEZ DE fetch MANUAL
          const response = await authenticatedFetch("" + API_BASE_URL + "/api/martin-pedido-tabela/", {
            method: "POST",
            body: JSON.stringify({ 
              dias: dias,
              selectedDate: formattedDate 
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const { df, dates } = data;

          const validDates = dates
            .filter(d => typeof d === 'string')
            .map(d => dayjs(d));

          setRowData(df);
          setBackendDates(validDates);
          
        } catch (err) {
          console.error('Erro ao carregar dados:', err);
          if (err.message.includes('Token')) {
            logout(); // Faz logout se houver problema com token
          } else {
            alert("Erro ao carregar a tabela inicial.");
          }
        } finally {
          setLoading(false);
        }
      }
    };
    fetchInitialData();
  }, [dias, selectedDate, authenticatedFetch, logout]);

  useEffect(() => {
    const processEstoqueFile = async () => {
      if (estoqueFile) {
        setLoading(true);
        const formData = new FormData();
        formData.append("estoque", estoqueFile);
    
        try {
          // USANDO authenticatedFetch PARA UPLOAD DE ARQUIVO
          const response = await authenticatedFetch("" + API_BASE_URL + "/api/martin-pedido-estoque/", {
            method: "POST",
            body: formData, // Para FormData, não especificamos Content-Type
            headers: {} // Deixa vazio para FormData
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
    
          const newRowData = rowData.map(row => {
            const updatedRow = data.find(d => d.Produtos === row.Produtos);
            if (updatedRow) {
              const newEstoque = updatedRow.Estoque;
              const newSaida = row.Total - newEstoque;
              const newMedia = (row.Saida1 + row.Saida2 + row.Saida3 + newSaida) / 4;
              let mult;
              const diasDivididoPorSete = dias / 7; 
              let newPelaMedia;
              let newPelaSaida;
              
              if(row.Manter != 0){
                newPelaSaida = row.Manter - newEstoque;
                newPelaMedia = row.Manter - newEstoque;
              } else{
                if (diasDivididoPorSete >= 0) {
                  mult = Math.floor(diasDivididoPorSete * 100) / 100;
                } else {
                  mult = Math.ceil(diasDivididoPorSete * 100) / 100;
                }
                let Arre_newSaida_mult;
                let calculo = (newSaida * mult);

                if (calculo >= 0) {
                  Arre_newSaida_mult = Math.ceil(calculo);
                } else {
                  Arre_newSaida_mult = Math.floor(calculo);
                }
                
                calculo = (Arre_newSaida_mult - newEstoque) / row.Fator;

                if (calculo >= 0) {
                  newPelaSaida = Math.ceil(calculo);
                } else {
                  newPelaSaida = Math.floor(calculo);
                }          
          
                calculo = ((newMedia * mult) - newEstoque) / row.Fator
                if (calculo >= 0) {
                  newPelaMedia = Math.ceil(calculo);
                } else {
                  newPelaMedia = Math.floor(calculo);
                }
              }
    
              return {
                ...row,
                Estoque: newEstoque,
                Saida: newSaida,
                'Pela Saida': newPelaSaida,
                'Pela Media': newPelaMedia,
                Media: newMedia
              };
            }
            return row;
          });
    
          setRowData(newRowData);
    
        } catch (err) {
          console.error('Erro ao processar estoque:', err);
          if (err.message.includes('Token')) {
            logout();
          } else {
            alert("Erro ao processar arquivo de estoque.");
          }
        } finally {
          setLoading(false);
        }
      }
    };
    processEstoqueFile();
  }, [estoqueFile, dias, rowData, authenticatedFetch, logout]);

  useEffect(() => {
    const processSwfastFile = async () => {
      if (swfastFile) {
        setLoading(true);
        const formData = new FormData();
        formData.append("swfast", swfastFile);
        
        try {
          // USANDO authenticatedFetch PARA UPLOAD DE ARQUIVO
          const response = await authenticatedFetch("" + API_BASE_URL + "/api/martin-pedido-swfast/", {
            method: "POST",
            body: formData,
            headers: {} // Deixa vazio para FormData
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const apiData = await response.json();
  
          const swfastArray = Object.entries(apiData).map(([produto, swfastValue]) => ({
            Produtos: produto,
            SWFAST: swfastValue
          }));
  
          const newRowData = rowData.map(row => {
            const updatedRow = swfastArray.find(d => d.Produtos === row.Produtos);
            return updatedRow ? { ...row, SWFAST: updatedRow.SWFAST } : row;
          });
  
          setRowData(newRowData);
        } catch (err) {
          console.error('Erro ao processar SWFAST:', err);
          if (err.message.includes('Token')) {
            logout();
          } else {
            alert("Erro ao processar arquivo SWFAST.");
          }
        } finally {
          setLoading(false);
        }
      }
    };
    processSwfastFile();
  }, [swfastFile, rowData, authenticatedFetch, logout]);

  const shouldDisableDate = (date) => {
    if (date.isAfter(today) || date.isSame(today, 'day')) {
        return false;
    }
    const isBackendDate = backendDates.some(bd => bd.isSame(date, 'day'));
    return !isBackendDate;
  };

  const onCellKeyDown = (params) => {
    if (params.event.key === 'Tab' && !params.event.shiftKey) {
        params.event.preventDefault();
        params.event.stopPropagation();
        params.event.stopImmediatePropagation();
        
        const { api, rowIndex, column } = params;
        const nextRowIndex = rowIndex + 1;
        const rowCount = api.getDisplayedRowCount();
        
        if (nextRowIndex < rowCount) {
            api.stopEditing(false);
            
            setTimeout(() => {
                api.setFocusedCell(nextRowIndex, column);
                setTimeout(() => {
                    api.startEditingCell({
                        rowIndex: nextRowIndex,
                        colKey: column.colId
                    });
                }, 50);
            }, 10);
        }
    }
  };

  const handleSave = async () => {
    if (!gridApi) {
      alert("Grid não está pronto para salvar.");
      return;
    }

    setLoading(true);
    try {
      const rowDataToSave = [];
      gridApi.forEachNode(node => rowDataToSave.push(node.data));

      const formattedDate = selectedDate.format('YYYY-MM-DD');

      // USANDO authenticatedFetch PARA SALVAR
      const response = await authenticatedFetch("" + API_BASE_URL + "/api/martin-pedido-salvar/", {
        method: "PUT",
        body: JSON.stringify({
          selectedDate: formattedDate,
          tableData: rowDataToSave,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao salvar a tabela.");
      }

      alert("Tabela salva com sucesso!");
    } catch (err) {
      console.error("Erro ao salvar:", err);
      if (err.message.includes('Token')) {
        logout();
      } else {
        alert(`Erro ao salvar a tabela: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Resto do código permanece igual...
  const onCellValueChanged = (params) => {
    const { colDef, data, api } = params;
    console.log(rowData)
    if (colDef.field === 'Fator') {
      const row = data;
      if(data.Manter != 0){
        const updatedRow = {
          ...row,
          'Pela Saida': data.Manter - data.Estoque,
          'Pela Media': data.Manter - data.Estoque,
        };

        const rowNode = api.getRowNode(data.id);
        if (rowNode) {
          rowNode.setData(updatedRow);
        } else {
          const newRowData = rowData.map(r => r.Produtos === row.Produtos ? updatedRow : r);
          setRowData(newRowData);
        }
      } else{
        const newFator = parseFloat(data.Fator);
        const newEstoque = row.Estoque;
        const newSaida = row.Total - newEstoque;
        const newMedia = (row.Saida1 + row.Saida2 + row.Saida3 + newSaida) / 4;
        const diasDivididoPorSete = dias / 7;
        const mult = diasDivididoPorSete >= 0 ? Math.floor(diasDivididoPorSete * 100) / 100 : Math.ceil(diasDivididoPorSete * 100) / 100;

        const calculoSaida = newSaida * mult;
        const Arre_newSaida_mult = calculoSaida >= 0 ? Math.ceil(calculoSaida) : Math.floor(calculoSaida);
        const newPelaSaidaRaw = (Arre_newSaida_mult - newEstoque) / newFator;
        const newPelaSaida = newPelaSaidaRaw >= 0 ? Math.ceil(newPelaSaidaRaw) : Math.floor(newPelaSaidaRaw);

        const calculoMedia = (newMedia * mult) - newEstoque;
        const newPelaMediaRaw = calculoMedia / newFator;
        const newPelaMedia = newPelaMediaRaw >= 0 ? Math.ceil(newPelaMediaRaw) : Math.floor(newPelaMediaRaw);

        const updatedRow = {
          ...row,
          'Pela Saida': newPelaSaida,
          'Pela Media': newPelaMedia,
        };

        const rowNode = api.getRowNode(data.id);
        if (rowNode) {
          rowNode.setData(updatedRow);
        } else {
          const newRowData = rowData.map(r => r.Produtos === row.Produtos ? updatedRow : r);
          setRowData(newRowData);
        }
      }
    } else if (colDef.field === 'Estoque') {
        // ... resto da lógica de Estoque igual
    }
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper elevation={3} sx={{ padding: 1, width: 'auto', minWidth: 600, alignSelf: 'flex-start', height: '260px' }}>
        <Box display="flex" gap={1} flexWrap={{ xs: 'wrap', md: 'nowrap' }} alignItems="center">
        <FileUpload label="Arquivo do estoque" onFileProcessed={setEstoqueFile} />
        <FileUpload label="Relatórios Descritivos - Vendas de Insumos - Selecionar o Período e XLSX" onFileProcessed={setSwfastFile} />
        <TextField
            label="Dias"
            type="number"
            value={dias}
            onChange={(e) =>
              setDias(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))
            }
            InputProps={{ inputProps: { min: 1, max: 90 } }}
            sx={{ width: 80 }}
          />
        </Box>
        </Paper>

        <Paper elevation={3} sx={{ padding: 1, width: 'auto', minWidth: 200, alignSelf: 'flex-start', height: '100' }}>
          <Box display="flex" gap={1} flexWrap={{ xs: 'wrap', md: 'nowrap' }} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <DatePicker
                label="Selecione a data"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                shouldDisableDate={shouldDisableDate}
              />
            </LocalizationProvider>      

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={loading}
            >
              Salvar Tabela
            </Button> 
          </Box>
        </Paper>                 

      <Box className="ag-theme-alpine" sx={{ height: 800, width: 1270 }}>
        {loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}   
            onGridReady={(params) => {
              setGridApi(params.api);
              setGridColumnApi(params.columnApi);
            }}
            getRowId={params => params.data.Ordem}
            onCellKeyDown={onCellKeyDown}
            onCellValueChanged={onCellValueChanged}
          />
        )}
      </Box>
    </Box>
  );
}